

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const DepartureDateDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [departure, setDeparture] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchDepartureData();
    }, [id]);

    const fetchDepartureData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/departure-dates/${id}`);
            setDeparture(response.data);
        } catch (error) {
            console.error('Error fetching departure details:', error);
            setSnackbar({ open: true, message: 'Error loading departure details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setDeparture(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/departure-dates/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Departure date updated successfully', severity: 'success' });
            fetchDepartureData();
        } catch (error) {
            console.error('Error updating departure date:', error);
            setSnackbar({ open: true, message: 'Error updating departure date', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this departure date?')) {
            try {
                await axiosInstance.delete(`/departure-dates/${id}`);
                setSnackbar({ open: true, message: 'Departure date deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/departure-dates'), 1000);
            } catch (error) {
                console.error('Error deleting departure date:', error);
                setSnackbar({ open: true, message: 'Error deleting departure date', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({
                open: true,
                message: `Unable to find user information for ${userName}.`,
                severity: 'info',
            });
        }
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => {
                                        const userId = item.field === 'created_by_name' ? departure.created_by : departure.updated_by;
                                        handleUserClick(userId, departure[item.field]);
                                    }}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{
                                        cursor: 'pointer',
                                        ml: 1
                                    }}
                                >
                                    {capitalizeFirstLetter(departure[item.field] || '')}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={departure?.[item.field]}
                                options={item.options || null}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                isMultiSelect={item.isMultiSelect}
                                renderValue={item.renderValue || ((val) => (val ? String(val) : '-'))}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!departure) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><MDTypography>Departure Date not found</MDTypography></MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">{departure.name}</MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>Departure Details</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <IconButton onClick={() => { setIsEditing(false); setUpdatedFields({}); fetchDepartureData(); }} sx={commonIconButtonStyles}><Close sx={commonIconStyles} /></IconButton>
                                            <IconButton onClick={handleSaveChanges} sx={commonIconButtonStyles}><Save sx={commonIconStyles} /></IconButton>
                                        </>
                                    ) : (
                                        <IconButton onClick={() => setIsEditing(true)} sx={commonIconButtonStyles}><Edit sx={commonIconStyles} /></IconButton>
                                    )}
                                    <IconButton onClick={handleDelete} sx={commonIconButtonStyles}><Delete sx={commonIconStyles} /></IconButton>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Departure ID/Name", field: "name" },
                                    { label: "Trip", field: "trip_id", readOnly: true, renderValue: (val) => departure.trip?.name || val },
                                    { label: "Departure Date", field: "departure_date", renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Return Date", field: "return_date", renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Departure City", field: "departure_city" },
                                    { label: "Book By Date", field: "book_by_date", renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Allocation Secured", field: "allocation_secured", renderValue: (val) => val ? "Yes" : "No" },
                                ])}

                                {renderSection("Inventory & Capacity", [
                                    { label: "Total Allocation", field: "total_allocation" },
                                    { label: "Hold", field: "hold" },
                                    { label: "Sold", field: "sold", readOnly: true },
                                    { label: "Available", field: "available", readOnly: true },
                                    { label: "Min Pax", field: "minimum_pax" },
                                    { label: "Max Pax", field: "maximum_pax" },
                                ])}

                                {renderSection("Financials & Deadlines", [
                                    { label: "High Season Surcharge", field: "high_season_surcharge" },
                                    { label: "BC Due Date", field: "bc_due_date", renderValue: (val) => formatDate(val, 'date') },
                                    { label: "TP Due Date", field: "tp_due_date", renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Trip Title Snapshot", field: "trip_title_snapshot" },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default DepartureDateDetail;
